// Per-tenant snapshot for a superadmin: how big is this tenant right now
// (active vehicles, active drivers), what are they actually paying for
// (enabled modules), and roughly what that comes to. The price is a live
// estimate off CURRENT counts and the Pricing tab's config -- there's no
// billing-cycle tracking yet, so it's a single "if we billed them right
// now" figure, not a locked-in this-month vs next-month split. Read-only
// otherwise -- toggling modules stays on the Tenants & Modules tab.
import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { Building2, Car, Users, Boxes, ShieldAlert } from 'lucide-react';
import { moduleIcon } from '@/lib/moduleIcons';
import { calculateBillableUnits, type PlatformBillingBasis } from '@/lib/billableUnits';
import { calculateTenantMonthlyPrice, type PricingTierInput, type ModulePricingConfig, type DiscountTierInput } from '@/lib/tenantPricing';
import { CORE_MODULE_KEYS } from '@/lib/coreModules';

interface TenantRow {
  id: string;
  tenant_name: string;
  trading_name: string | null;
  is_active: boolean;
  platform_billing_basis: PlatformBillingBasis;
}

interface EnabledModule {
  module_key: string;
  module_name: string;
  enabled_at: string;
}

interface TenantStats {
  activeVehicles: number;
  activeDrivers: number;
  enabledModules: EnabledModule[];
}

const SuperadminAnalytics: React.FC = () => {
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [statsByTenantId, setStatsByTenantId] = useState<Record<string, TenantStats>>({});
  const [tiers, setTiers] = useState<PricingTierInput[]>([]);
  const [moduleConfigs, setModuleConfigs] = useState<ModulePricingConfig[]>([]);
  const [discountTiers, setDiscountTiers] = useState<DiscountTierInput[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [
        { data: tenantRows, error: tenantErr },
        { data: vehicleRows, error: vehicleErr },
        { data: driverRows, error: driverErr },
        { data: moduleRows, error: moduleErr },
        { data: tenantModuleRows, error: tenantModuleErr },
        { data: tierRows, error: tierErr },
        { data: discountRows, error: discountErr },
      ] = await Promise.all([
        supabase
          .from('tenants')
          .select('id, tenant_name, trading_name, is_active, platform_billing_basis')
          .order('tenant_name'),
        supabase.from('vehicles').select('tenant_id, status'),
        supabase.from('drivers').select('tenant_id, is_active'),
        supabase.from('app_modules').select('module_key, module_name, billing_basis, excluded_from_module_discount'),
        supabase.from('tenant_modules').select('tenant_id, module_key, created_at').eq('is_enabled', true),
        supabase.from('pricing_module_tiers').select('module_key, rate_type, tier_min, tier_max, unit_price'),
        supabase.from('pricing_module_count_discounts').select('min_modules, max_modules, discount_percent'),
      ]);
      const err = tenantErr || vehicleErr || driverErr || moduleErr || tenantModuleErr || tierErr || discountErr;
      if (err) {
        toast({ title: 'Error', description: 'Failed to load analytics: ' + err.message, variant: 'destructive' });
        setLoading(false);
        return;
      }

      const moduleNameByKey: Record<string, string> = {};
      (moduleRows || []).forEach((m: { module_key: string; module_name: string }) => {
        moduleNameByKey[m.module_key] = m.module_name;
      });

      const stats: Record<string, TenantStats> = {};
      (tenantRows || []).forEach((t: TenantRow) => {
        stats[t.id] = { activeVehicles: 0, activeDrivers: 0, enabledModules: [] };
      });
      (vehicleRows || []).forEach((v: { tenant_id: string | null; status: string | null }) => {
        if (!v.tenant_id || !stats[v.tenant_id]) return;
        if ((v.status || '').trim().toLowerCase() === 'active') stats[v.tenant_id].activeVehicles += 1;
      });
      (driverRows || []).forEach((d: { tenant_id: string | null; is_active: boolean }) => {
        if (!d.tenant_id || !stats[d.tenant_id]) return;
        if (d.is_active) stats[d.tenant_id].activeDrivers += 1;
      });
      (tenantModuleRows || []).forEach((tm: { tenant_id: string; module_key: string; created_at: string }) => {
        if (!stats[tm.tenant_id]) return;
        stats[tm.tenant_id].enabledModules.push({
          module_key: tm.module_key,
          module_name: moduleNameByKey[tm.module_key] || tm.module_key,
          enabled_at: tm.created_at,
        });
      });

      setTenants((tenantRows as TenantRow[]) || []);
      setStatsByTenantId(stats);
      setTiers((tierRows as PricingTierInput[]) || []);
      setModuleConfigs((moduleRows as ModulePricingConfig[]) || []);
      setDiscountTiers((discountRows as DiscountTierInput[]) || []);
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading analytics...</p>;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {tenants.map((t) => {
        const stats = statsByTenantId[t.id] ?? { activeVehicles: 0, activeDrivers: 0, enabledModules: [] };
        const { billableUnits, guardApplied } = calculateBillableUnits(
          t.platform_billing_basis,
          stats.activeDrivers,
          stats.activeVehicles
        );
        const billedOnVehicles = t.platform_billing_basis === 'vehicles';
        const paidModules = stats.enabledModules.filter((m) => !CORE_MODULE_KEYS.includes(m.module_key));
        const price = calculateTenantMonthlyPrice(
          paidModules,
          tiers,
          moduleConfigs,
          discountTiers,
          t.platform_billing_basis,
          stats.activeDrivers,
          stats.activeVehicles
        );
        return (
          <Card key={t.id} className="shadow-lg bg-card border border-border">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base text-foreground flex items-center gap-2 min-w-0">
                  <Building2 className="h-4 w-4 text-yellow-500 shrink-0" />
                  <span className="truncate">{t.trading_name || t.tenant_name}</span>
                </CardTitle>
                {!t.is_active && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700 text-gray-300 shrink-0">Inactive</span>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <div
                  className={`flex flex-col items-center justify-center gap-1 rounded-lg py-3 ${
                    billedOnVehicles ? 'bg-yellow-900/15 ring-1 ring-yellow-600/40' : 'bg-secondary/30'
                  }`}
                >
                  <Car className="h-4 w-4 text-yellow-500" />
                  <span className="text-lg font-bold text-foreground tabular-nums">{stats.activeVehicles}</span>
                  <span className="text-[10px] text-muted-foreground">Vehicles{billedOnVehicles ? ' (billed)' : ''}</span>
                </div>
                <div
                  className={`flex flex-col items-center justify-center gap-1 rounded-lg py-3 ${
                    !billedOnVehicles ? 'bg-yellow-900/15 ring-1 ring-yellow-600/40' : 'bg-secondary/30'
                  }`}
                >
                  <Users className="h-4 w-4 text-yellow-500" />
                  <span className="text-lg font-bold text-foreground tabular-nums">{stats.activeDrivers}</span>
                  <span className="text-[10px] text-muted-foreground">Drivers{!billedOnVehicles ? ' (billed)' : ''}</span>
                </div>
                <div className="flex flex-col items-center justify-center gap-1 rounded-lg bg-secondary/30 py-3">
                  <Boxes className="h-4 w-4 text-yellow-500" />
                  <span className="text-lg font-bold text-foreground tabular-nums">{stats.enabledModules.length}</span>
                  <span className="text-[10px] text-muted-foreground">Modules</span>
                </div>
              </div>

              <div
                className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 ${
                  guardApplied ? 'bg-red-900/10 border border-red-900/30 text-red-300' : 'bg-secondary/20 text-muted-foreground'
                }`}
              >
                {guardApplied && <ShieldAlert className="h-3.5 w-3.5 shrink-0" />}
                <span>
                  Billable base units: <span className="font-semibold tabular-nums">{billableUnits}</span>
                  {guardApplied && ` -- floored up from ${billedOnVehicles ? stats.activeVehicles : stats.activeDrivers} raw ${t.platform_billing_basis} by the ${billedOnVehicles ? 'driver' : 'vehicle'} ratio guard`}
                </span>
              </div>

              <div className="rounded-lg border border-yellow-900/30 bg-yellow-900/10 px-3 py-2.5 flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Est. monthly price</p>
                  <p className="text-xl font-bold text-yellow-500 tabular-nums leading-tight">${price.total.toFixed(2)}</p>
                </div>
                {price.discountPercent > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-900/20 border border-green-900/40 text-green-400 shrink-0">
                    {price.discountPercent}% multi-module off
                  </span>
                )}
              </div>

              {stats.enabledModules.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {stats.enabledModules.map((m) => {
                    const Icon = moduleIcon(m.module_key);
                    return (
                      <span
                        key={m.module_key}
                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-yellow-900/10 border border-yellow-900/30 text-yellow-500"
                      >
                        <Icon className="h-3 w-3" />
                        {m.module_name}
                      </span>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
      {tenants.length === 0 && <p className="text-sm text-muted-foreground">No tenants found.</p>}
    </div>
  );
};

export default SuperadminAnalytics;
